import { type Picture } from '@/components/atoms/AppImage';
import { type Client } from '@/content/clients';
import { type LogoBox } from '@/utils/logoBox';
import { slugify } from '@/utils/slug';

/**
 * Client logos, resolved from whatever happens to be in `src/assets/clients/`.
 *
 * Unlike `brandLogos` this is not a fixed list of imports. The roster in
 * `content/clients.ts` runs to dozens of organisations and the artwork arrives
 * one file at a time, from whoever has it, so a missing file must not break the
 * build and a new file must not need a code change. Drop `mtn-uganda.png` in
 * the folder and the tile picks it up on the next build; until then
 * `ClientMark` renders the client's monogram.
 *
 * Most of the roster has no artwork yet, so the common case here is
 * `undefined` and the fallback is the normal state of a tile rather than an
 * error path.
 *
 * Raster sources are trimmed of their padding and resized at build time to the
 * tile's 56px logo height (1x and 2x), then served as AVIF/WebP with a PNG
 * fallback, so a 2000px press-kit logo never reaches the browser. `fit=inside`
 * is required with `trim`: imagetools sizes from the untrimmed aspect ratio, and
 * its default `cover` would crop the trimmed mark. SVGs ship as-is — imagetools
 * does not process them, so they are imported as plain URLs.
 */
const rasterLogos = import.meta.glob<Picture>(
  '../assets/clients/*.{png,PNG,jpg,JPG,jpeg,webp,WEBP,avif,tiff,gif}',
  {
    eager: true,
    query: '?trim&h=56;112&fit=inside&format=avif;webp;png&as=picture',
    import: 'default',
  },
);

const svgLogos = import.meta.glob<string>('../assets/clients/*.{svg,SVG}', {
  eager: true,
  query: '?url',
  import: 'default',
});

/**
 * File names are not ours to dictate — logos arrive as `URA-logo.png` or
 * `stanbic-bank-acacia.png` — so the name is reduced to a comparable key rather
 * than matched literally: lower-cased, hyphenated, and with the `logo` most
 * downloads carry dropped.
 */
const logoKey = (path: string) =>
  slugify(
    path
      .split('/')
      .pop()
      ?.replace(/\.[^.]+$/, '') ?? '',
  )
    .replace(/-logos?$/, '')
    .replace(/^logos?-/, '');

/** Raster last, so a hand-made PNG wins over an SVG of the same name. */
const logoIndex = new Map<string, Picture | string>([
  ...Object.entries(svgLogos).map(([path, url]) => [logoKey(path), url] as const),
  ...Object.entries(rasterLogos).map(([path, picture]) => [logoKey(path), picture] as const),
]);

/**
 * The logo for a client, or `undefined` if none has been supplied yet.
 *
 * A client's slug is tried first, then the short names it is actually known by
 * (`URA`, `NHL`), then a file name that is one whole word longer or shorter
 * than the key in either direction — which is what matches
 * `stanbic-bank-acacia.png` to Stanbic Bank and `roko.png` to Roko
 * Construction. The alternative is renaming every file that arrives to suit our
 * code, which is a rule nobody outside this repository will ever follow.
 *
 * Matching stops at a word boundary deliberately: `roko` matching
 * `roko-construction` is the same name, `unbs` matching `unbsug` would be a
 * guess, so that one is declared as an alias instead.
 *
 * Two guards keep that convenience from handing a tile the wrong company's
 * mark, which is a worse failure than showing no mark at all:
 *
 * - A client may name its `logoKey` outright, and then only that key is tried,
 *   matched exactly. "Rwenzori" is a prefix of "Rwenzori Rare Metals Limited",
 *   so without it the mining company would wear the water brand's logo.
 * - A fuzzy key matching more than one file is treated as no match. With
 *   a roster this size and the files arriving named by whoever had them, the
 *   next collision is a question of when; an ambiguous one must fail visibly
 *   as a monogram rather than quietly pick whichever file enumerated first.
 */
export function clientLogo(client: Client): Picture | string | undefined {
  // Checked before any lookup: the file exists and is the reason this flag is
  // set, so finding it would be the bug. See `Client.logoPending`.
  if (client.logoPending) return undefined;
  if (client.logoKey) return logoIndex.get(client.logoKey);

  const keys = [client.slug, ...(client.aliases ?? []).map(slugify)];
  for (const key of keys) {
    const exact = logoIndex.get(key);
    if (exact) return exact;

    const near = [...logoIndex].filter(
      ([candidate]) => candidate.startsWith(`${key}-`) || key.startsWith(`${candidate}-`),
    );
    if (near.length === 1) return near[0]![1];
  }
  return undefined;
}

/**
 * Whether artwork has actually been supplied for a client.
 *
 * `useClientLogoRows` needs this to balance the marquee's two rows by artwork,
 * so that one row is not all logos and the other all monograms. It is a
 * question about the roster rather than about one tile, which is why it is
 * here and not answered by rendering: the hook has to know before it deals.
 */
export const hasClientLogo = (client: Client): boolean => clientLogo(client) !== undefined;

/**
 * Matches `h=56` above. Both figures are larger than a car badge's: these are
 * mostly wordmarks with fine print under them, and at the 44px a make's badge
 * needs, a name like "Uganda Revenue Authority" is present but unreadable.
 */
export const CLIENT_LOGO_BOX: LogoBox = { maxWidth: 140, maxHeight: 56, area: 3800 };
